import React from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import RelatedContent from './RelatedContent'
import { useHelpContext } from './hooks'
import {
  SpacerVertical,
  TitleSecondary,
  ParagraphSecondary,
  LinkSecondary
} from 'mage-deps/@klarna/bubble-ui'
import { CMSEntry, CMSEntries } from 'mage-cms'
import { EducationAnalytics } from 'education-tools'
import TopicList from './TopicList'
import Feedback from './Feedback'
import { path } from 'ramda'

function isExternalLink(url) {
  return !(url.startsWith(window.location.origin) || url.startsWith('/'))
}

export default function ArticlePage() {
  const {
    navigation,
    navigation: {
      activePage: {
        parameters: { articleSlug: slug }
      }
    }
  } = useHelpContext()

  return (
    <CMSEntry contentType="UHQMArticle" filters={{ slug }}>
      {article => (
        <div>
          <EducationAnalytics
            category="uhqm/page/article"
            action="pageview"
            label={article.title}
            referenceType="article"
            referenceId={article.id}
          />
          <div>
            <TitleSecondary id="uhqm-article-title">{article.title}</TitleSecondary>
            <div>{documentToReactComponents(article.body, renderOptions(navigation))}</div>
          </div>
          <SpacerVertical medium />
          <CMSEntries contentType="UHQMTopic" filters={{ links_to_entry: article.id }}>
            {topics => <TopicList topics={topics} analyticsCategory="uhqm/topic-title/article" />}
          </CMSEntries>
          <SpacerVertical large />
          <RelatedContent
            articleId={article.id}
            analyticsCategory="uhqm/article-title/related/article"
          />

          <SpacerVertical large />
          <Feedback articleId={article.id} articleTitle={article.title} />
        </div>
      )}
    </CMSEntry>
  )
}

const getImageUrl = path(['data', 'target', 'file', 'url'])

export function renderOptions(navigation) {
  return {
    renderNode: {
      // eslint-disable-next-line react/display-name
      [BLOCKS.PARAGRAPH]: (node, children) => <ParagraphSecondary>{children}</ParagraphSecondary>,
      // eslint-disable-next-line react/display-name
      [INLINES.ENTRY_HYPERLINK]: (node, children) => (
        <EducationAnalytics action="click">
          {track => (
            <LinkSecondary
              onClick={() => {
                const { contentType, slug, id } = node.data.target
                switch (contentType) {
                  case 'UHQMTour':
                    track({
                      category: 'uhqm/article-content-link/tour',
                      label: slug,
                      referenceType: 'tour',
                      referenceId: id
                    })
                    navigation.push('tour', { tourSlug: slug })
                    break
                  case 'UHQMTopic':
                    track({
                      category: 'uhqm/article-content-link/topic',
                      label: slug,
                      referenceType: 'topic',
                      referenceId: id
                    })
                    navigation.push('topic', { topicSlug: slug })
                    break
                  case 'UHQMArticle':
                    track({
                      category: `uhqm/article-content-link/article`,
                      label: slug,
                      referenceType: 'article',
                      referenceId: id
                    })
                    navigation.push('article', { articleSlug: slug })
                    break
                }
              }}
              inParagraph
              medium
            >
              {children}
            </LinkSecondary>
          )}
        </EducationAnalytics>
      ),
      // eslint-disable-next-line react/display-name
      [INLINES.HYPERLINK]: (node, children) => {
        if (node.data.uri === 'help-page:change-of-bad') {
          return (
            <LinkSecondary
              onClick={() => {
                navigation.push('bad')
              }}
              inParagraph
              medium
            >
              {children}
            </LinkSecondary>
          )
        }

        return (
          <LinkSecondary
            target={isExternalLink(node.data.uri) ? '_blank' : '_self'}
            href={node.data.uri}
            inParagraph
            medium
          >
            {children}
          </LinkSecondary>
        )
      },
      // eslint-disable-next-line react/display-name
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        return (
          <LinkSecondary target="_blank" href={node.data.target.file.url} inParagraph medium>
            {children}
          </LinkSecondary>
        )
      },
      // eslint-disable-next-line react/display-name
      [BLOCKS.EMBEDDED_ASSET]: (node, children) => (
        <img
          style={{ maxWidth: '100%', marginTop: 5 }}
          src={getImageUrl(node)}
          {...(node.data.target.description ? { alt: node.data.target.description } : {})}
        />
      )
    }
  }
}
